import { useState } from "react";
import { BadgeCheck, FileText } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { mediaUrl } from "../lib/api";
import { fmtGregorian, fmtHijri } from "../lib/format";
import { Lightbox } from "./Gallery";
import { Reveal } from "./Common";

function Media({ media, title }) {
  const { tr } = useI18n();
  const [open, setOpen] = useState(null);
  const images = media.filter((m) => m.type === "image").map((m) => m.url);
  return (
    <div className="mt-5 space-y-4">
      {media.filter((m) => m.type === "video").map((m) => (
        <video key={m.url} data-testid="progress-video" controls preload="metadata" playsInline className="aspect-video w-full max-w-2xl rounded-sm border border-fg/10 bg-overlay">
          <source src={`${m.url}#t=0.1`} type="video/mp4" />
        </video>
      ))}
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {images.map((u, i) => (
            <button key={u} type="button" data-testid={`progress-image-${i}`} onClick={() => setOpen(i)} className="overflow-hidden rounded-sm border border-fg/10">
              <img src={mediaUrl(u)} alt={`${title} ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105" />
            </button>
          ))}
        </div>
      )}
      {media.filter((m) => m.type === "pdf").map((m) => (
        <a key={m.url} href={m.url} target="_blank" rel="noopener noreferrer" data-testid="progress-pdf-link" className="btn-ghost !py-2.5"><FileText className="h-4 w-4" />{tr(m, "caption")}</a>
      ))}
      <Lightbox images={images} index={open} onClose={() => setOpen(null)} title={title} />
    </div>
  );
}

export default function ProgressTimeline({ updates }) {
  const { t, tr, lang } = useI18n();
  if (!updates?.length) {
    return <p data-testid="progress-coming-soon" className="glass rounded-sm p-8 text-center text-fg/60">{t("project.progressSoon")}</p>;
  }
  return (
    <ol data-testid="progress-timeline" className="relative space-y-12 border-s border-cta/30 ps-8">
      {updates.map((u, i) => (
        <Reveal as="li" key={u.date + i} className="relative" delay={i * 0.05}>
          <span className="absolute -start-[41px] top-1 h-4 w-4 rounded-full border-2 border-canvas bg-cta ring-4 ring-cta/20" aria-hidden="true" />
          <p data-testid={`progress-date-${i}`} className="text-sm text-cta"><time dateTime={u.date}>{fmtGregorian(u.date, lang)}</time> · <span className="text-fg/50">{fmtHijri(u.date, lang)}</span></p>
          <h3 className="mt-2 font-display text-2xl text-fg">{tr(u, "title")}</h3>
          {u.verified && <p className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-300"><BadgeCheck className="h-3.5 w-3.5 text-cta" /><span className="text-fg/60">{t("project.verified")}</span></p>}
          {tr(u, "description") && <p className="mt-3 max-w-2xl text-sm text-fg/60">{tr(u, "description")}</p>}
          <Media media={u.media} title={tr(u, "title")} />
        </Reveal>
      ))}
    </ol>
  );
}
